import { Flex, FormControl, Heading } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useCallback, useMemo } from "react";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { LoginConfig } from "src/generated/hooks/org-config/requests";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import * as OrgService from "src/services/orgService";
import * as RD from "src/types/remoteData";
import { z } from "zod";

export const Form = () => {
  const formik = useFormikContext<FormikType>();
  return (
    <Flex direction="column" gap={2}>
      <Heading as="h3" variant="admin">
        Login
      </Heading>
      <FormikSwitchableContent<"enabled" | "disabled">
        title="Login"
        label="Login methods"
        description="Configure the login methods for your organization."
        checked={["enabled", ""]}
        unchecked={["disabled", ""]}
        showContent={(value) => value === "enabled"}
        name="login.status"
      >
        <FormControl
          // isInvalid={!!meta.error}
          height="400px"
          display="flex"
          flexDirection="column"
        >
          <JsonEditor
            hideToolbar
            height="100%"
            schema={{}}
            filename=""
            text={formik.values.login.json}
            onChange={(newValue: any) => {
              formik.setFieldValue("login.json", newValue);
            }}
          />
          {/* {meta.error && <FormErrorMessage>Invalid config</FormErrorMessage>} */}
        </FormControl>
      </FormikSwitchableContent>
    </Flex>
  );
};

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const { remoteData, update } = OrgService.useOrgConfig<LoginConfig>("Login");
  const initialValues = useMemo(() => {
    return remoteData.map((config): FormikType => {
      if (config?.disabled === false) {
        return {
          login: {
            status: "enabled",
            json: JSON.stringify(
              {
                parent: config.parent,
                admin: config.admin,
              },
              null,
              2
            ),
          },
        };
      }
      return {
        login: {
          status: "disabled",
          json: JSON.stringify(
            {
              parent: OrgConfig.LoginConfigSchema.default.parent,
              admin: OrgConfig.LoginConfigSchema.default.admin,
            },
            null,
            2
          ),
        },
      };
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      const json = JSON.parse(values.login.json);

      if (values.login.status === "enabled") {
        await update({
          disabled: false,
          ...json,
        });
      } else {
        await update({
          admin: [...OrgConfig.LoginConfigSchema.default.admin],
          parent: [...OrgConfig.LoginConfigSchema.default.parent],
        });
      }
    },
    [update]
  );
  return { initialValues, submit };
}

export const FormikSchema = z.object({
  login: z.object({
    status: z.union([z.literal("enabled"), z.literal("disabled")]),
    json: z.string(),
  }),
});

type FormikType = z.infer<typeof FormikSchema>;
